var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { cn } from "../../lib/utils.js";
import { Check, ChevronRight, X } from "lucide-react";
var StepIcon = function (_a) {
    var status = _a.status, className = _a.className;
    var baseClasses = "w-4 h-4 rounded-full flex items-center justify-center shrink-0";
    switch (status) {
        case 'complete':
            return (_jsx("div", { className: cn(baseClasses, 
                // Light: dark green disc + cream check. Dark: lime disc + dark check (matches primary CTAs).
                "bg-[hsl(var(--primary-pressed-dark))] border-2 border-[hsl(var(--primary-pressed-dark))] dark:bg-primary dark:border-primary relative", className), children: _jsx(Check, { className: "w-2.5 h-2.5 text-[#FAF4F0] dark:text-primary-foreground absolute", strokeWidth: 3 }) }));
        case 'error':
            return (_jsx("div", { className: cn(baseClasses, "bg-[#9E0000] dark:bg-destructive border-2 border-[#9E0000] dark:border-destructive relative", className), children: _jsx(X, { className: "w-2.5 h-2.5 text-[#FAF4F0] dark:text-background absolute", strokeWidth: 3 }) }));
        case 'current':
            return (_jsx("div", { className: cn(baseClasses, "bg-transparent border-2 border-[hsl(var(--primary-pressed-dark))] dark:border-primary", className) }));
        case 'incomplete':
        default:
            return (_jsx("div", { className: cn(baseClasses, "bg-white dark:bg-background border-2 border-[#A09D9B] dark:border-muted-foreground", className) }));
    }
};
var StepLabel = function (_a) {
    var label = _a.label, status = _a.status, onClick = _a.onClick;
    var getTextColor = function () {
        switch (status) {
            case 'complete':
            case 'current':
                return 'text-[#242424] dark:text-foreground';
            case 'error':
                return 'text-[#9E0000] dark:text-destructive';
            case 'incomplete':
            default:
                return 'text-[#757271] dark:text-muted-foreground';
        }
    };
    var getFontWeight = function () {
        switch (status) {
            case 'complete':
            case 'current':
                return 'font-medium';
            case 'error':
            case 'incomplete':
            default:
                return 'font-normal';
        }
    };
    return (_jsx("span", { className: cn("text-sm leading-[18.2px]", getTextColor(), getFontWeight(), onClick && "cursor-pointer hover:opacity-80"), onClick: onClick, children: label }));
};
var Connector = function (_a) {
    var orientation = _a.orientation, status = _a.status, nextStatus = _a.nextStatus;
    // Determine connector color based on current step status
    var getConnectorColor = function () {
        if (status === 'complete')
            return 'border-[#CCC8C6] dark:border-input';
        if (status === 'error')
            return 'border-[#CCC8C6] dark:border-input';
        return 'border-[#CCC8C6] dark:border-input';
    };
    if (orientation === 'horizontal') {
        return (_jsx("div", { className: cn("w-3 h-3 flex items-center justify-center"), children: _jsx(ChevronRight, { className: cn("w-3.75 h-3.75 text-[#585755] dark:text-muted-foreground"), strokeWidth: 1.25 }) }));
    }
    else {
        return (_jsx("div", { className: "relative", style: { width: '5.5px', height: '32px' }, children: _jsx("div", { className: "absolute border-r border-[#CCC8C6] dark:border-input", style: {
                    width: '5.5px',
                    height: '32px',
                    left: '7.5px' // Center the line with the 16px circle
                } }) }));
    }
};
var Stepper = React.forwardRef(function (_a, ref) {
    var className = _a.className, steps = _a.steps, _b = _a.orientation, orientation = _b === void 0 ? 'horizontal' : _b, _c = _a.showConnectors, showConnectors = _c === void 0 ? true : _c, _d = _a.showChevrons, showChevrons = _d === void 0 ? true : _d, props = __rest(_a, ["className", "steps", "orientation", "showConnectors", "showChevrons"]);
    if (orientation === 'horizontal') {
        return (_jsx("div", __assign({ ref: ref, className: cn('flex items-center gap-4', className) }, props, { children: steps.map(function (step, index) {
                var _a;
                return (_jsxs(React.Fragment, { children: [_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx(StepIcon, { status: step.status }), _jsx(StepLabel, { label: step.label, status: step.status, onClick: step.onClick })] }), showConnectors && showChevrons && index < steps.length - 1 && (_jsx(Connector, { orientation: "horizontal", status: step.status, nextStatus: (_a = steps[index + 1]) === null || _a === void 0 ? void 0 : _a.status }))] }, step.id));
            }) })));
    }
    else {
        return (_jsx("div", __assign({ ref: ref, className: cn('flex flex-col justify-center items-start', className) }, props, { children: steps.map(function (step, index) { return (_jsx("div", { className: "flex justify-start items-start", children: _jsxs("div", { className: "flex flex-col justify-start items-start", children: [_jsxs("div", { className: "relative flex justify-start items-center gap-1.5", children: [_jsx(StepIcon, { status: step.status }), _jsx(StepLabel, { label: step.label, status: step.status, onClick: step.onClick })] }), showConnectors && index < steps.length - 1 && (_jsx("div", { style: {
                                width: '8px',
                                height: '32px',
                                borderRight: '1px solid #CCC8C6'
                            } }))] }) }, step.id)); }) })));
    }
});
Stepper.displayName = "Stepper";
export { Stepper };
